<template>
  <CollapsibleSection title="Formation" icon="fas fa-graduation-cap" context="quote" storage-key="formation-info" :bare="bare">
    <DetailItem label="Catégorie" :copyable="false">
      <select
        v-if="isEditing"
        v-model="editedQuote.formation_categorie"
        @change="$emit('category-change')"
        class="form-control"
        :disabled="loadingFormations"
      >
        <option value="">-- Sélectionner une catégorie --</option>
        <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
      </select>
      <template v-else>
        <Badge v-if="!isMissing(quote.formation_categorie)" :style="getCategoryBadgeStyle(quote.formation_categorie)">
          {{ quote.formation_categorie }}
        </Badge>
        <DetailEmpty v-else />
      </template>
      <small v-if="isEditing && loadingFormations">Chargement des catégories...</small>
    </DetailItem>
    <DetailItem v-if="getValue('formation_categorie')" label="Formation">
      <!-- La valeur est l'id catalogue et non le nom : deux formations peuvent porter
           le même intitulé et ne différer que par la durée (3H00 / 7H00). -->
      <select
        v-if="isEditing"
        v-model="editedQuote.formation_catalogue_id"
        @change="$emit('formation-change')"
        class="form-control"
        :disabled="loadingFormations"
      >
        <option value="">-- Sélectionner une formation --</option>
        <option v-for="formation in availableFormations" :key="formation.ID" :value="formation.ID">
          {{ nomAvecDuree(formation) }}
        </option>
      </select>
      <template v-else>
        <span v-if="!isMissing(quote.formation_nom)">{{ quote.formation_nom }}</span>
        <DetailEmpty v-else />
      </template>
      <small v-if="isEditing && loadingFormations">Chargement des formations...</small>
      <small v-else-if="isEditing && getValue('formation_categorie') && availableFormations.length === 0" class="warning-small">
        Aucune formation trouvée pour cette catégorie.
      </small>
    </DetailItem>
    <!-- Durée et effectif ne s'affichent plus : ils se choisissent. Ensemble ils
         désignent une formule du catalogue, c'est-à-dire ce qui est vendu. Une
         formation sans grille retombe sur son ancienne durée en texte. -->
    <!-- « Par journée », une seule question remplace durée, taille et nombre de
         groupes : quelle formule (1 journée · jusqu'à 36 personnes — 6
         sessions — 2 790 €). -->
    <DetailItem v-if="getValue('formation_nom') && parJournee" label="Formule" :copyable="false">
      <select
        v-if="isEditing"
        :value="formuleJourneeChoisie"
        @change="choisirFormuleJournee($event.target.value)"
        aria-label="Formule"
        class="form-control"
      >
        <option value="">-- Sélectionner une formule --</option>
        <option v-for="formule in formulesJournee" :key="formule.id" :value="formule.id">
          {{ formule.libelle }}
        </option>
      </select>
      <template v-else>
        <span v-if="libelleFormuleJournee">{{ libelleFormuleJournee }}</span>
        <DetailEmpty v-else />
      </template>
      <small v-if="isEditing && alerteEffectif" class="warning-small">{{ alerteEffectif }}</small>
      <small v-if="isEditing && rappelPrix" class="warning-small">{{ rappelPrix }}</small>
    </DetailItem>
    <DetailItem v-if="getValue('formation_nom') && !parJournee" label="Durée" :copyable="false">
      <select
        v-if="isEditing && dureesDisponibles.length"
        :value="dureeChoisie"
        @change="choisirDuree($event.target.value)"
        aria-label="Durée de la formation"
        class="form-control"
      >
        <option value="">-- Sélectionner une durée --</option>
        <option v-for="duree in dureesDisponibles" :key="duree.valeur" :value="duree.valeur">
          {{ duree.libelle }}
        </option>
      </select>
      <template v-else>
        <span v-if="!isMissing(dureeAffichee)">{{ dureeAffichee }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <!--
      Combien de personnes, en groupes de quelle taille, et combien de groupes :
      les trois questions qui décident du prix, dans le même ordre que sur le
      formulaire public. Le nombre de participants vivait plus bas, après le
      stationnement du formateur.
    -->
    <!--
      Le lieu vient avant l'effectif et les groupes : c'est lui qui désigne le
      champ d'effectif lu (intra ou inter) et qui décide si un découpage en
      groupes a seulement un sens. Le demander après donnait un « Pour 0
      participants » tant que la section Inter, plus bas, n'était pas remplie.
    -->
    <DetailItem v-if="getValue('formation_nom')" label="Lieu">
      <select
        v-if="isEditing"
        v-model="editedQuote.formation_lieu"
        @change="$emit('lieu-change')"
        class="form-control"
      >
        <option value="">-- Sélectionner --</option>
        <option
          v-for="option in formationLieuOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <template v-else>
        <span v-if="!isMissing(quote.formation_lieu)">{{ quote.formation_lieu }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <!--
      L'effectif se saisit ici quel que soit le lieu : on en a besoin juste
      au-dessus des groupes (intra) et il multiplie le tarif à la place
      (inter). Seule la colonne écrite change — `champEffectif` la désigne.
      Le bloc de la session INTER, juste en dessous, ne le redemande pas.
    -->
    <DetailItem v-if="getValue('formation_nom')" label="Nombre de participants">
      <input
        v-if="isEditing"
        v-model.number="editedQuote[champEffectif]"
        aria-label="Nombre de participants"
        class="form-control"
        type="number"
        min="1"
        placeholder="Nombre de participants"
      />
      <!--
        `getValue` et non `quote` : une section confirmee repasse en lecture
        alors que l'edition continue. Lire la valeur enregistree y affichait
        l'ancien effectif — « 10 participants » sous un tarif calcule pour 40,
        et a cote d'un nombre de groupes, lui, a jour.
      -->
      <template v-else>
        <span v-if="!isMissing(getValue(champEffectif))">{{ getValue(champEffectif) }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <!--
      Taille des groupes : une notion d'intra seulement. Le tarif inter se vend
      au stagiaire et n'a qu'une colonne par durée — la tranche n'entre pas
      dans son calcul, donc la faire choisir en inter suggérerait un arbitrage
      qui n'existe pas.
    -->
    <DetailItem
      v-if="getValue('formation_nom') && enIntra && !parJournee"
      label="Taille des groupes"
      :copyable="false"
    >
      <select
        v-if="isEditing && dureesDisponibles.length"
        :value="trancheChoisie"
        @change="choisirTranche($event.target.value)"
        aria-label="Taille des groupes"
        class="form-control"
        :disabled="!dureeChoisie"
      >
        <option value="">{{ invitationTranche }}</option>
        <option v-for="tranche in tranchesDisponibles" :key="tranche.cle" :value="tranche.cle">
          {{ tranche.min === tranche.max ? `${tranche.min} personnes` : `${tranche.min} à ${tranche.max} personnes` }}{{ tranche.retiree ? ` ${MENTION_RETIREE}` : '' }}
        </option>
      </select>
      <template v-else>
        <span v-if="!isMissing(effectifAffiche)">{{ effectifAffiche }}</span>
        <DetailEmpty v-else />
      </template>
      <small v-if="isEditing && alerteEffectif" class="warning-small">{{ alerteEffectif }}</small>
    </DetailItem>
    <DetailItem
      v-if="getValue('formation_nom') && trancheChoisie && enIntra && !parJournee"
      label="Nombre de groupes"
      :copyable="false"
    >
      <input
        v-if="isEditing"
        :value="nombreGroupes"
        @input="choisirNombreGroupes($event.target.value)"
        aria-label="Nombre de groupes"
        class="form-control"
        type="number"
        min="1"
      />
      <span v-else>{{ nombreGroupes }}</span>
      <small v-if="isEditing" class="hint-small">{{ aideGroupes }}</small>
      <small v-if="isEditing && rappelPrix" class="warning-small">{{ rappelPrix }}</small>
    </DetailItem>
    <!-- Champs propres à l'analyse du besoin, injectés par le tunnel (divider inclus) -->
    <slot name="besoin-extra" />
    <!-- Ce que le client a écrit lui-même en demandant son devis. Il vivait
         dans « Lieu de la formation », où il ne se lisait qu'après le
         stationnement du formateur : c'est pourtant la demande, et il se
         complète au téléphone. Une seule saisie, ici, pour tous les écrans. -->
    <DetailItem label="Commentaire / Période souhaitée / Besoins spécifiques" wide>
      <Textarea
        v-if="isEditing"
        v-model="editedQuote.lieu_formation_commentaire"
        no-wrapper
        :rows="3"
        aria-label="Commentaire / Période souhaitée / Besoins spécifiques"
        placeholder="Commentaire du client concernant ses besoins spécifiques (contexte, contraintes, attentes…)"
      />
      <!-- `getValue` et non `quote` : une section confirmée repasse en lecture,
           et c'est la saisie en cours qu'il faut alors relire, pas la version
           d'avant. `pre-wrap` : le client écrit souvent sur plusieurs lignes. -->
      <template v-else>
        <span v-if="!isMissing(getValue('lieu_formation_commentaire'))" style="white-space: pre-wrap;">{{ getValue('lieu_formation_commentaire') }}</span>
        <DetailEmpty v-else />
      </template>
    </DetailItem>
    <template #footer>
      <slot name="footer" />
    </template>
  </CollapsibleSection>
</template>

<script setup>
import { computed } from 'vue'
import { useSelectionFormule } from '../../composables/quotes/useSelectionFormule.js'
import { CollapsibleSection, DetailItem, DetailEmpty } from '../data'
import { Badge, Textarea } from '../ui'
// `nomAvecDuree` et non `formatFormationName` : la migration laisse
// volontairement des formations homonymes au catalogue, que seule leur durée
// distingue. La liste en affichait deux fois « gestes et postures travail sur
// écran », sans rien pour les départager.
import { nomAvecDuree } from '../../utils/formationUtils.js'
import { MENTION_RETIREE } from '../../utils/formuleFormation.js'
import { getCategoryBadgeStyle } from '../../utils/categoryColors.js'

const props = defineProps({
  /** Rend la section sans carte ni en-tête, pour l'imbriquer dans un bloc regroupé. */
  bare: {
    type: Boolean,
    default: false
  },
  quote: {
    type: Object,
    required: true
  },
  isEditing: {
    type: Boolean,
    default: false
  },
  editedQuote: {
    type: Object,
    default: () => ({})
  },
  loadingFormations: {
    type: Boolean,
    default: false
  },
  categories: {
    type: Array,
    default: () => []
  },
  availableFormations: {
    type: Array,
    default: () => []
  },
  selectedFormation: {
    type: Object,
    default: null
  },
  formationLieuOptions: {
    type: Array,
    default: () => []
  },
  getValue: {
    type: Function,
    required: true
  }
})

defineEmits(['category-change', 'formation-change', 'lieu-change'])

const isMissing = (value) => {
  return !value || value === '' || value === null || value === undefined
}

// ─── Choix de la formule : une durée, puis un effectif ────────────────────

const {
  parJournee,
  formulesJournee,
  formuleJourneeChoisie,
  choisirFormuleJournee,
  libelleFormuleJournee,
  dureesDisponibles,
  tranchesDisponibles,
  trancheChoisie,
  choisirTranche,
  nombreGroupes,
  choisirNombreGroupes,
  groupesConseilles,
  palierRetenu,
  effectifDemande,
  champEffectif,
  enIntra,
  dureeChoisie,
  invitationTranche,
  choisirDuree,
  rappelPrix,
  dureeAffichee,
  effectifAffiche,
  alerteEffectif
} = useSelectionFormule(props)

/**
 * Ce que l'effectif appelle, et ce qui se passe au-delà du barème. Le
 * commercial doit pouvoir dire d'où sort le prix qu'il envoie.
 */
const aideGroupes = computed(() => {
  const conseil = groupesConseilles(trancheChoisie.value)
  const base = `Pour ${effectifDemande.value || 0} participants, il en faut ${conseil}.`
  const dernier = palierRetenu.value?.nbGroupes || 1
  return nombreGroupes.value > dernier && dernier > 1
    ? `${base} Au-delà de ${dernier} groupes, c'est le meilleur tarif du barème qui s'applique.`
    : base
})
</script>

<style scoped>
.warning-small {
  color: var(--color-warning-dark);
  display: block;
  margin-top: 0.25rem;
}

/* Une indication, pas une alerte : elle explique le nombre proposé sans
   prétendre que quelque chose ne va pas. */
.hint-small {
  color: var(--color-text-tertiary);
  display: block;
  margin-top: 0.25rem;
}
</style>
